<template>
  <VRow justify="center">
    <ValidationObserver
        ref="signUp"
        v-slot="{ invalid }"
        name="signUp"
    >
      <VCol
          class="px-2 pb-3 d-flex align-center pt-0"
          cols="12"
      >
        <div>
          <span class="body-1">
            {{ $t('authForm.phoneNumber') }}
          </span>
        </div>
        <div class="ml-2">
          <span class="body-bold">
            {{ phone }}
          </span>
        </div>
        <div class="ml-2">
          <VBtn
              small
              class="box-shadow-elevation-4"
              data-test="edit-phone-button"
              @click="$emit('toStartStep')"
          >
            <span class="small-button">
              {{ $t('general.edit') }}
            </span>
          </VBtn>
        </div>
      </VCol>
      <VCol
          class="px-2 d-flex py-0"
          cols="12"
      >
        <span class="body-2">
          {{ $t('authForm.fillBasicInfo') }}
        </span>
      </VCol>
      <VCol
          class="px-2 py-0"
          cols="12"
      >
        <ValidationProvider
            ref="firstName"
            v-slot="{ errors, ariaInput }"
            name="firstName"
            :rules="{ required: true, max: 255, atLeastTwoLetters: /[a-zA-Z]{2,}/, onlyLettersAndSymbols: firstSecondNameRegex }"
            mode="aggressive"
        >
          <VTextField
              v-bind="ariaInput"
              v-model="firstName"
              name="regex"
              data-test="first-name-input"
              :hide-details="!errors.length"
              :error="!!errors.length"
              :error-messages="validationErrorResolve(errors[0])"
              :label="$t('authForm.firstName')"
          />
        </ValidationProvider>
      </VCol>
      <VCol
          class="px-2 py-0"
          cols="12"
      >
        <ValidationProvider
            ref="lastName"
            v-slot="{ errors, ariaInput }"
            name="lastName"
            :rules="{ required: true, max: 255, atLeastTwoLetters: /[a-zA-Z]{2,}/, onlyLettersAndSymbols: firstSecondNameRegex }"
            mode="aggressive"
        >
          <VTextField
              v-bind="ariaInput"
              v-model="lastName"
              data-test="last-name-input"
              :hide-details="!errors.length"
              :error="!!errors.length"
              :error-messages="validationErrorResolve(errors[0])"
              :label="$t('authForm.lastName')"
          />
        </ValidationProvider>
      </VCol>
      <VCol
          class="px-2 py-0"
          cols="12"
      >
        <ValidationProvider
            ref="password"
            v-slot="{ errors, ariaInput }"
            name="password"
            :rules="{ required: true, min: 6, max: 40, withoutSpaces: /^\S*$/ }"
            mode="aggressive"
        >
          <VTextField
              v-bind="ariaInput"
              v-model="password"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              data-test="password-input"
              :hide-details="!errors.length"
              :label="$t('authForm.password')"
              :error="!!errors.length"
              :error-messages="validationErrorResolve(errors[0])"
              class="body-1"
              @click:append="showPassword = !showPassword"
              @input="isPasswordCorrect = true"
          />
        </ValidationProvider>
      </VCol>
      <VCol
          class="px-2 py-0"
          cols="12"
      >
        <ValidationProvider
            ref="email"
            v-slot="{ errors, ariaInput }"
            name="email"
            :rules="{ email: true }"
            mode="aggressive"
        >
          <VTextField
              v-model="email"
              data-test="email-input"
              :hide-details="!errors.length"
              :label="$t('authForm.email')"
              :error="!!errors.length"
              :error-messages="$t(errors[0])"
          />
        </ValidationProvider>
      </VCol>
      <VCol
          class="px-2"
          cols="12"
      >
        <div class="d-flex align-items-center">
          <ValidationProvider
              ref="agreement"
              v-slot="{ ariaInput }"
              name="agreement"
              :rules="{ required: { allowFalse: false } }"
              :allowFalse="false"
              class="checkbox-wrapper"
              mode="aggressive"
          >
            <VCheckbox v-bind="ariaInput" v-model="isAgree" data-test="agreement-checkbox" />
          </ValidationProvider>
          <div>
          <span class="body-1 mr-1"><span class="error--text" aria-hidden="true">* </span>{{ $t('authForm.agreement.iAmAgree') }}</span>
          <a 
            :href="privacyPolicyPageName" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="body-1 text-decoration-none secondary--text"
            data-test="privacy-policy-link"
          >
            {{ $t('checkout.agreement.privacyPolicy') }}
          </a>
          <span class="body-1 mx-1">{{ $t('authForm.agreement.and')}}</span>
          <a 
            :href="termsAndConditionsPageName" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="body-1 text-decoration-none secondary--text"
            data-test="terms-conditions-link"
          >
            {{ $t('checkout.agreement.termsAndConditions') }}
          </a>
          </div>
        </div>
      </VCol>
      <VCol
          class="px-2 pb-3 pt-0"
          cols="12"
          col="12"
      >
        <VBtn
            class="w-100 border-radius-8"
            dark
            data-test="sign-up-button"
            :class="{'disabled': invalid }"
            large
            color="brandingPrimary"
            @click="onSubmit"
        >
          <span class="button">
            {{ $t('general.next') }}
          </span>
        </VBtn>
      </VCol>
    </ValidationObserver>
  </VRow>
</template>

<script lang="ts">
export default {
  name: 'SignUpStep',
  // Kept in Options data so template refs (firstName/lastName/email/password) stay on $refs.
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    };
  },
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import Constants from '@slg/web-customer-shared/src/utils/constants';
import { useValidationError, useVm } from '../../../composables';

const props = defineProps({
  phone: {
    type: String,
    default: '',
  },
  termsAndConditionsPageName: {
    type: String,
    default: null,
  },
  privacyPolicyPageName: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['trackGtmEventForNonRegisteredUser', 'toVerificationStep']);

const { validationErrorResolve } = useValidationError();
const vm = useVm() as {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const isAgree = ref(false);
const showPassword = ref(false);
const firstSecondNameRegex = Constants.FIRST_SECOND_NAME_REGEX;

function onSubmit() {
  const customerDetails = {
    firstName: vm.firstName.trim(),
    lastName: vm.lastName.trim(),
    email: vm.email,
    password: vm.password,
    phone: props.phone,
  };
  emit('trackGtmEventForNonRegisteredUser');
  emit('toVerificationStep', customerDetails);
}
</script>

<style lang="scss" scoped>
.checkbox-wrapper {
  .v-input {
    padding-bottom: unset !important;
  }
}
</style>
